import React, { useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Cookies from "js-cookie";

interface UserPreferencesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type OnboardingOption = "talent" | "company" | null;

export default function UserPreferencesModal({ isOpen, onClose }: Readonly<UserPreferencesModalProps>) {
  const [selectedOption, setSelectedOption] = useState<OnboardingOption>(null);
  const router = useRouter();

  if (!isOpen) return null;

  const handleSelect = () => {
    if (selectedOption) {
      // Guardar en cookies (expira en 365 días)
      Cookies.set('userPreference', selectedOption, { expires: 365 });

      // Redireccionar según la selección
      if (selectedOption === 'talent') {
        router.push('/landing/jobbers');
      } else if (selectedOption === 'company') {
        router.push('/landing/company');
      }

      // Cerrar el modal
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - not dismissable */}
      <div
        className="absolute inset-0 backdrop-blur-sm bg-black/20"
      />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-[600px] max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-4 sm:p-6 pb-2 sm:pb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-[#1A1A2E] text-center">
            What brings you here today?
          </h2>
        </div>

        {/* Content Area */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6">
          {/* Selection Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {/* Talent Acquisition Card */}
            <div>
              <button
                onClick={() => setSelectedOption("talent")}
                className={`relative rounded-3xl transition-all p-4 sm:p-8 text-center hover:shadow-lg w-full ${selectedOption === "talent"
                    ? ""
                    : "border-2 border-[#EDEEFE] hover:border-[#C4C4E8]"
                  }`}
                style={{
                  backgroundImage: `url(/${selectedOption === "talent" ? "talent_acquisition_gradient.png" : "cuadrícula.png"})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {/* Radio indicator */}
                <div className="absolute top-4 left-4 sm:top-6 sm:left-6 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-[#EDEEFE] bg-white flex items-center justify-center">
                  {selectedOption === "talent" && (
                    <div
                      className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full"
                      style={{
                        background: 'linear-gradient(83deg, #5B02FF 14.86%, #8F36FF 98.61%)'
                      }}
                    ></div>
                  )}
                </div>

                {/* Icon */}
                <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 mt-4 sm:mt-8 rounded-2xl bg-white shadow-md flex items-center justify-center">
                  <Image src="/emojis/🚀.png" alt="Briefcase" width={64} height={64} className="object-contain w-10 h-10 sm:w-16 sm:h-16" />
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-semibold text-[#1A1A2E] mb-1 sm:mb-2">
                  Talent
                </h3>
                <p className="text-xs sm:text-sm text-black">
                  I’m looking to find my next role.
                </p>
              </button>
            </div>

            {/* Global Payroll Card */}
            <div>
              <button
                onClick={() => setSelectedOption("company")}
                className={`relative rounded-3xl transition-all p-4 sm:p-8 text-center hover:shadow-lg w-full ${selectedOption === "company"
                    ? ""
                    : "border-2 border-[#EDEEFE] hover:border-[#C4C4E8]"
                  }`}
                style={{
                  backgroundImage: `url(/${selectedOption === "company" ? "global_payroll_gradient.png" : "cuadrícula.png"})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}
              >
                {/* Radio indicator */}
                <div className="absolute top-4 left-4 sm:top-6 sm:left-6 w-5 h-5 sm:w-6 sm:h-6 rounded-full border-2 border-[#EDEEFE] bg-white flex items-center justify-center">
                  {selectedOption === "company" && (
                    <div
                      className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5 rounded-full"
                      style={{
                        background: 'linear-gradient(83deg, #5B02FF 14.86%, #8F36FF 98.61%)'
                      }}
                    ></div>
                  )}
                </div>

                {/* Icon */}
                <div className="w-16 h-16 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 mt-4 sm:mt-8 rounded-2xl bg-white shadow-md flex items-center justify-center">
                  <Image src="/emojis/tower.png" alt="Globe" width={64} height={64} className="object-contain w-10 h-10 sm:w-16 sm:h-16" />
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-semibold text-[#1A1A2E] mb-1 sm:mb-2">
                  Company
                </h3>
                <p className="text-xs sm:text-sm text-black">
                  I want to hire and onboard workers.
                </p>
              </button>
            </div>
          </div>


          {/* Button Area */}
          <button
            onClick={handleSelect}
            disabled={!selectedOption}
            className={`w-full py-2 sm:py-3 rounded-lg font-medium transition text-sm mt-4 sm:mt-6 ${selectedOption
                ? "text-white shadow-lg"
                : "bg-gray-50 text-gray-600 cursor-not-allowed border-2 border-gray-200"
              }`}
            style={selectedOption ? {
              background: 'linear-gradient(83deg, #5B02FF 14.86%, #8F36FF 98.61%)'
            } : undefined}
          >
            Select
          </button>
        </div>

      </div>
    </div>
  );
}
